import { z } from 'zod';
import { DomainError } from '../errors.js';
import { addDays, compareLocalDate, type LocalDate, parseLocalDate } from '../local-date.js';
import { toIssues } from '../materials/catalog.js';
import type { ParseResult } from '../voyage/schemas.js';

/**
 * Ölçüm noktası, limit, ölçüm, durum tetikleyicisi ve trend (plan M7 Task 15; ADR 0029 §7; K18,
 * K19, K21). Saf modül: hata iletileri koddur, arayüz çevirir. Limitler bakım programının
 * parçasıdır [UR Z20 1.3.3, UR Z27 2.1.3]; ölçüm değerlendirildiği limitleri taşır ve sonucu sonra
 * değişmez [NR496 4.5.2].
 */

/** Limitin dayanağı: üretici, tanınmış standart, CM sağlayıcısı, klas, şirket [UR Z27 1.3.2, 1.4.3]. */
export const LIMIT_SOURCES = ['oem', 'standard', 'cm_provider', 'class', 'company'] as const;
export type LimitSource = (typeof LIMIT_SOURCES)[number];

/** Sonuç: normal (PASS), uyarı bandı dışı (ABNORMAL), tehlike sınırı dışı (FAIL). */
export const MEASUREMENT_RESULTS = ['normal', 'abnormal', 'fail'] as const;
export type MeasurementResult = (typeof MEASUREMENT_RESULTS)[number];

/** Ölçümün kaynağı: elle giriş ya da CM içe aktarma (K20). */
export const MEASUREMENT_SOURCES = ['manual', 'cm_import'] as const;
export type MeasurementSource = (typeof MEASUREMENT_SOURCES)[number];

/**
 * Limitler: uyarı bandı (`alert*`) normal aralıktır; dışı ABNORMAL. Tehlike sınırı (`danger*`)
 * uyarı bandının dışındadır; dışı FAIL. Her yanın dahil ya da hariç işareti vardır (K18): "a–b"
 * ve "±" dahil, ">" ve "<" hariç.
 */
export interface MeasurementLimits {
  alertLow: number | null;
  alertHigh: number | null;
  dangerLow: number | null;
  dangerHigh: number | null;
  lowInclusive: boolean;
  highInclusive: boolean;
}

/** Ölçümün taşıdığı limit anlık kopyası (değerlendirmenin girdisi). */
export interface LimitsSnapshot extends MeasurementLimits {
  unit: string;
  limitSource: LimitSource;
}

const BOUNDS = ['alertLow', 'alertHigh', 'dangerLow', 'dangerHigh'] as const;

/**
 * Limitlerin düzeni: en az bir sınır (`LIMITS_REQUIRED`); her alt sınır her üst sınırdan küçük;
 * tehlike sınırı uyarı bandının dışında (`INVALID_LIMITS`).
 */
export function assertLimits(limits: MeasurementLimits): void {
  const values = BOUNDS.map((key) => limits[key]);
  if (values.every((value) => value === null)) throw new DomainError('LIMITS_REQUIRED');
  if (values.some((value) => value !== null && !Number.isFinite(value))) {
    throw new DomainError('INVALID_LIMITS', 'not finite');
  }
  const lows = [limits.dangerLow, limits.alertLow].filter((v): v is number => v !== null);
  const highs = [limits.alertHigh, limits.dangerHigh].filter((v): v is number => v !== null);
  if (lows.some((low) => highs.some((high) => !(low < high)))) {
    throw new DomainError('INVALID_LIMITS', 'low ≥ high');
  }
  if (limits.dangerLow !== null && limits.alertLow !== null && limits.dangerLow > limits.alertLow) {
    throw new DomainError('INVALID_LIMITS', 'danger low inside alert band');
  }
  if (
    limits.dangerHigh !== null &&
    limits.alertHigh !== null &&
    limits.dangerHigh < limits.alertHigh
  ) {
    throw new DomainError('INVALID_LIMITS', 'danger high inside alert band');
  }
}

function within(value: number, low: number | null, high: number | null, limits: MeasurementLimits) {
  const aboveLow = low === null || (limits.lowInclusive ? value >= low : value > low);
  const belowHigh = high === null || (limits.highInclusive ? value <= high : value < high);
  return aboveLow && belowHigh;
}

/**
 * Değerlendirme (K18; PMS belgesi §8, §11): tehlike sınırı dışında FAIL, uyarı bandı dışında
 * ABNORMAL, yoksa normal. Sınırın dahil ya da hariç işareti uygulanır.
 */
export function evaluateMeasurement(value: number, limits: MeasurementLimits): MeasurementResult {
  if (!Number.isFinite(value)) throw new DomainError('INVALID_VALUE', String(value));
  if (!within(value, limits.dangerLow, limits.dangerHigh, limits)) return 'fail';
  if (!within(value, limits.alertLow, limits.alertHigh, limits)) return 'abnormal';
  return 'normal';
}

/** Noktanın limitlerinin kanonik anlık kopyası: anahtar sırası sabit, ölçüme yazılır. */
export function limitsSnapshotOf(point: LimitsSnapshot): LimitsSnapshot {
  return {
    unit: point.unit,
    limitSource: point.limitSource,
    alertLow: point.alertLow,
    alertHigh: point.alertHigh,
    dangerLow: point.dangerLow,
    dangerHigh: point.dangerHigh,
    lowInclusive: point.lowInclusive,
    highInclusive: point.highInclusive,
  };
}

const sameSnapshot = (a: LimitsSnapshot, b: LimitsSnapshot) =>
  JSON.stringify(limitsSnapshotOf(a)) === JSON.stringify(limitsSnapshotOf(b));

/* ---------- Ölçüm noktası şeması ---------- */

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const collapse = (value: string) => value.normalize('NFC').trim().replace(/\s+/g, ' ');
const text = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().min(1, 'REQUIRED').max(max, 'TOO_LONG'));
const optionalText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);
const optionalNumber = () =>
  z
    .number(typed('INVALID_NUMBER'))
    .refine(Number.isFinite, 'INVALID_NUMBER')
    .nullish()
    .transform((value) => value ?? null);
const flag = (fallback: boolean) =>
  z
    .boolean(typed('INVALID_OPTION'))
    .nullish()
    .transform((value) => value ?? fallback);
const localDate = z.string(typed('INVALID_DATE')).refine((value) => {
  try {
    parseLocalDate(value);
    return true;
  } catch {
    return false;
  }
}, 'INVALID_DATE');

/**
 * Ölçüm noktası (plan Task 15, 18): birim zorunlu; limit kaynağı zorunlu [UR Z27 1.3.2, 1.4.3];
 * CM sağlayıcısının limiti sağlayıcının adını ister; tehlike sınırı uyarı bandının dışında
 * (`INVALID_LIMITS`); otomatik arıza kapatılırsa gerekçe (K19); baseline tarihiyle. İlk baseline
 * (`baseline_original`) sunucunun işidir ve sonra değişmez [UR Z27 2.3.2(iii)].
 */
export const measurementPointInputSchema = z
  .strictObject(
    {
      equipmentId: z.uuid(typed('INVALID_ID')),
      jobDefinitionId: z
        .uuid(typed('INVALID_ID'))
        .nullish()
        .transform((value) => value ?? null),
      name: text(200),
      unit: text(20),
      nominal: optionalNumber(),
      alertLow: optionalNumber(),
      alertHigh: optionalNumber(),
      dangerLow: optionalNumber(),
      dangerHigh: optionalNumber(),
      lowInclusive: flag(true),
      highInclusive: flag(true),
      limitSource: z.enum(LIMIT_SOURCES, typed('INVALID_OPTION')),
      provider: optionalText(200),
      baselineValue: optionalNumber(),
      baselineTakenOn: localDate.nullish().transform((value) => value ?? null),
      autoDefect: flag(true),
      autoDefectOffReason: optionalText(500),
      changeReason: optionalText(500),
      sortOrder: z
        .number(typed('INVALID_NUMBER'))
        .int('INVALID_NUMBER')
        .min(0, 'OUT_OF_RANGE')
        .max(100_000, 'OUT_OF_RANGE')
        .nullish()
        .transform((value) => value ?? 0),
      active: flag(true),
    },
    {
      error: (issue) =>
        issue.code === 'unrecognized_keys'
          ? 'UNRECOGNIZED_KEY'
          : missing(issue.input)
            ? 'REQUIRED'
            : 'INVALID_OBJECT',
    },
  )
  .superRefine((input, ctx) => {
    const issue = (path: string, message: string) =>
      ctx.addIssue({ code: 'custom', message, path: [path] });
    try {
      assertLimits(input);
    } catch (error) {
      issue('limits', error instanceof DomainError ? error.code : 'INVALID_LIMITS');
    }
    if (input.limitSource === 'cm_provider' && input.provider === null) {
      issue('provider', 'PROVIDER_REQUIRED');
    }
    // Otomatik arıza kapatılabilir, ama gerekçeyle (K19)
    if (!input.autoDefect && input.autoDefectOffReason === null) {
      issue('autoDefectOffReason', 'AUTO_DEFECT_OFF_REASON_REQUIRED');
    }
    if (input.baselineValue !== null && input.baselineTakenOn === null) {
      issue('baselineTakenOn', 'BASELINE_DATE_REQUIRED');
    }
  });
export type MeasurementPointInput = z.output<typeof measurementPointInputSchema>;

/** Ölçüm noktasını ayrıştırır; baseline tarihi gemi bugününden (`today`) sonra olamaz. */
export function parseMeasurementPointInput(
  input: unknown,
  options: { today?: LocalDate } = {},
): ParseResult<MeasurementPointInput> {
  const result = measurementPointInputSchema.safeParse(input);
  if (!result.success) return { ok: false, issues: toIssues(result.error) };
  const { baselineTakenOn } = result.data;
  if (options.today !== undefined && baselineTakenOn !== null && baselineTakenOn > options.today) {
    return { ok: false, issues: [{ path: 'baselineTakenOn', code: 'FUTURE_DATE' }] };
  }
  return { ok: true, value: result.data };
}

/** Değişiklik kuralının baktığı alanlar. */
export interface PointChangeSnapshot extends LimitsSnapshot {
  equipmentId: string;
  baselineValue: number | null;
  baselineOriginal: number | null;
  changeReason: string | null;
}

/** İlk baseline bir kez yazılır, sonra değişmez [UR Z27 2.3.2(iii)]. */
export function baselineOriginalFor(
  previousOriginal: number | null,
  baselineValue: number | null,
): number | null {
  return previousOriginal ?? baselineValue;
}

/**
 * Ölçüm noktası değişikliği (K20; UR Z27 2.2.4):
 * - Noktanın ekipmanı değişmez (`POINT_EQUIPMENT_FIXED`): ölçümler o ekipmana bağlıdır.
 * - Ölçümü olan noktanın birimi değişmez (`UNIT_LOCKED`): eski değerler başka birimdedir.
 * - Limit ya da limit kaynağı değişirse gerekçe bu değişiklikle verilir
 *   (`LIMIT_CHANGE_REASON_REQUIRED`); önceki değişikliğin gerekçesi yeniden sayılmaz.
 * - İlk baseline değişmez (`BASELINE_ORIGINAL_FIXED`); sonraki baseline değişikliği gerekçe ister
 *   (`BASELINE_CHANGE_REASON_REQUIRED`).
 * `previous` yoksa yeni noktadır: ilk baseline girilen baseline olmalıdır.
 */
export function assertPointChange(
  previous: PointChangeSnapshot | undefined,
  next: PointChangeSnapshot,
  context: { hasMeasurements: boolean },
): void {
  const expectedOriginal = baselineOriginalFor(
    previous?.baselineOriginal ?? null,
    next.baselineValue,
  );
  if (next.baselineOriginal !== expectedOriginal) {
    throw new DomainError(
      previous?.baselineOriginal != null ? 'BASELINE_ORIGINAL_FIXED' : 'BASELINE_ORIGINAL_MISMATCH',
      `${String(next.baselineOriginal)} ≠ ${String(expectedOriginal)}`,
    );
  }
  if (!previous) return;
  if (previous.equipmentId !== next.equipmentId) {
    throw new DomainError('POINT_EQUIPMENT_FIXED', next.equipmentId);
  }
  if (context.hasMeasurements && previous.unit !== next.unit) {
    throw new DomainError('UNIT_LOCKED', `${previous.unit} → ${next.unit}`);
  }
  const reasonGiven =
    next.changeReason !== null &&
    next.changeReason.trim() !== '' &&
    next.changeReason !== previous.changeReason;
  if (!sameSnapshot(previous, next) && !reasonGiven) {
    throw new DomainError('LIMIT_CHANGE_REASON_REQUIRED');
  }
  if (
    previous.baselineValue !== null &&
    previous.baselineValue !== next.baselineValue &&
    !reasonGiven
  ) {
    throw new DomainError('BASELINE_CHANGE_REASON_REQUIRED');
  }
}

/* ---------- Ölçüm ---------- */

export interface MeasurementInput {
  /** Ölçüm anı, ISO-8601 UTC. */
  measuredAt: string;
  /** Gemi saati farkı, dakika. */
  tzOffsetMin: number;
  /** Ölçüm günü: gemi yerel tarihi (CLAUDE.md "Zaman"). */
  localDate: LocalDate;
  value: number;
  unit: string;
  limitsSnapshot: LimitsSnapshot;
  result: MeasurementResult;
}

const localDateOf = (at: string, tzOffsetMin: number): LocalDate =>
  new Date(Date.parse(at) + tzOffsetMin * 60_000).toISOString().slice(0, 10);

/**
 * Ölçüm kuralı:
 * - Ölçüm günü ölçüm anının gemi yerel tarihidir (`LOCAL_DATE_MISMATCH`); an gemi sunucusu saatine
 *   göre gelecekte olamaz (`FUTURE_DATE`) [NR496 4.5.2a].
 * - Değer sonlu bir sayı (`INVALID_VALUE`); birim noktanınkiyle aynı (`UNIT_MISMATCH`).
 * - Limit anlık kopyası noktanın limitleridir (`LIMITS_MISMATCH`); sonuç kopyayla değerlendirilir
 *   (`RESULT_MISMATCH`). İkisini de sunucu doldurur; istemcinin gönderdiği farklıysa reddedilir.
 * - `point` verilmezse (bulutun gemi paketini uygulaması; K35) noktanın şimdiki sürümü sorulmaz:
 *   ölçüm kendi kopyasıyla tutarlı olmalıdır (birim ve sonuç). Gemideki sonuç korunur.
 */
export function validateMeasurement(
  input: MeasurementInput,
  context: { serverNow: string; point?: LimitsSnapshot },
): void {
  const at = Date.parse(input.measuredAt);
  if (Number.isNaN(at) || !Number.isInteger(input.tzOffsetMin)) {
    throw new DomainError('INVALID_DATE', String(input.measuredAt));
  }
  if (input.localDate !== localDateOf(input.measuredAt, input.tzOffsetMin)) {
    throw new DomainError('LOCAL_DATE_MISMATCH', input.localDate);
  }
  // [NR496 4.5.2a] gelecek tarihli parametre kaydı yok; saat gemi sunucusunun
  if (at > Date.parse(context.serverNow)) throw new DomainError('FUTURE_DATE', input.measuredAt);
  if (typeof input.value !== 'number' || !Number.isFinite(input.value)) {
    throw new DomainError('INVALID_VALUE', String(input.value));
  }
  const reference = context.point ?? input.limitsSnapshot;
  if (input.unit !== reference.unit) {
    throw new DomainError('UNIT_MISMATCH', `${input.unit} ≠ ${String(reference.unit)}`);
  }
  if (context.point && !sameSnapshot(input.limitsSnapshot, context.point)) {
    throw new DomainError('LIMITS_MISMATCH');
  }
  const expected = evaluateMeasurement(input.value, input.limitsSnapshot);
  if (input.result !== expected) {
    throw new DomainError('RESULT_MISMATCH', `${String(input.result)} ≠ ${expected}`);
  }
}

/* ---------- Durum tetikleyicisi (K21) ---------- */

/** Plan motorunun gördüğü ölçüm. */
export interface ConditionReading {
  id: string;
  pointId: string;
  /** İş emrine bağlı ölçüm (as-found / as-left); bağımsız ölçümde boş. */
  workOrderId: string | null;
  /** Ölçümün iş emrinin işi (planlı iş emrinde); bağımsız ya da plansız iş emrinde boş. */
  workOrderJobId?: string | null;
  /** Ölçüm anı, ISO-8601 UTC. */
  measuredAt: string;
  localDate: LocalDate;
  result: MeasurementResult;
  voided: boolean;
}

/** İşi vadeye getiren durum: tetikleyen nokta, son ölçüm ve eşik aşımının başladığı gün. */
export interface ConditionTrigger {
  pointId: string;
  measurementId: string;
  /** Kesintisiz ABNORMAL/FAIL dizisinin ilk ölçümünün günü: iş bu gün vadeye gelir. */
  since: LocalDate;
  /** Tetikleyen noktaların son ölçümlerinin en kötüsü. */
  result: 'abnormal' | 'fail';
}

/** Son yapılışın sınırı: kayıt anından, önceki sistemin gününden ya da sınırsız. */
export interface ConditionBase {
  /** Son yapılış kaydının anı: yalnız bundan sonra alınan ölçümler sayılır. */
  afterInstant?: string;
  /** Önceki sistemden gelen son yapılış günü: bu günden sonraki ölçümler sayılır. */
  afterLocalDate?: LocalDate;
  /** Son yapılış kaydının iş emri: o işin ölçümleri tetiklemez. */
  excludeWorkOrderId?: string | null;
  /**
   * İşin kendisi: işin kendi iş emirlerindeki ölçümler (as-found / as-left) işi tetiklemez ve
   * diziyi kapatmaz; başka işin (ör. periyodik ölçüm işi) iş emrindeki ölçüm sayılır.
   */
  excludeJobId?: string;
}

const byMeasured = (a: ConditionReading, b: ConditionReading) =>
  a.measuredAt.localeCompare(b.measuredAt) || a.id.localeCompare(b.id);

/**
 * Durum tetikleyicisi (K21; NR496 [4.4.2a] "parameter threshold"): işin ölçüm noktalarından
 * birinin, son yapılıştan sonra alınmış ve son yapılışın iş emrine bağlı olmayan iptal edilmemiş
 * son ölçümü ABNORMAL ya da FAIL ise iş vadeye gelir. Vade, o noktadaki kesintisiz ABNORMAL/FAIL
 * dizisinin ilk ölçümünün günüdür (en sıkı okuma: aşım geldiği gün iş vadededir). Birden çok
 * nokta tetiklerse en erken gün. İş yapıldıktan sonra eski ölçüm tetiklemez. İşin kendi iş
 * emirlerindeki ölçümler (as-found / as-left) işin tetikleyicisine girmez (ADR 0029 Ek M7c).
 */
export function conditionTrigger(
  pointIds: readonly string[],
  readings: readonly ConditionReading[],
  base: ConditionBase,
): ConditionTrigger | undefined {
  if (pointIds.length === 0) return undefined;
  const points = new Set(pointIds);
  const after = base.afterInstant === undefined ? undefined : Date.parse(base.afterInstant);
  const counted = readings
    .filter((r) => points.has(r.pointId) && !r.voided)
    .filter((r) => after === undefined || Date.parse(r.measuredAt) > after)
    .filter(
      (r) =>
        base.afterLocalDate === undefined || compareLocalDate(r.localDate, base.afterLocalDate) > 0,
    )
    .filter(
      (r) =>
        base.excludeWorkOrderId === undefined ||
        base.excludeWorkOrderId === null ||
        r.workOrderId !== base.excludeWorkOrderId,
    )
    .filter((r) => base.excludeJobId === undefined || r.workOrderJobId !== base.excludeJobId)
    .sort(byMeasured);
  let trigger: ConditionTrigger | undefined;
  for (const pointId of [...points].sort()) {
    const series = counted.filter((r) => r.pointId === pointId);
    const last = series.at(-1);
    if (!last || last.result === 'normal') continue;
    let first = last;
    for (let i = series.length - 1; i >= 0; i -= 1) {
      const reading = series[i] as ConditionReading;
      if (reading.result === 'normal') break;
      first = reading;
    }
    const result = last.result;
    if (
      !trigger ||
      compareLocalDate(first.localDate, trigger.since) < 0 ||
      (first.localDate === trigger.since && result === 'fail' && trigger.result !== 'fail')
    ) {
      trigger = {
        pointId,
        measurementId: last.id,
        since: first.localDate,
        result: trigger?.result === 'fail' || result === 'fail' ? 'fail' : 'abnormal',
      };
    } else if (result === 'fail') {
      trigger.result = 'fail';
    }
  }
  return trigger;
}

/* ---------- Trend tahmini (K21) [NR496 4.5.2 Not 1, 4.5.3a] ---------- */

/** Trend için en az ölçüm sayısı. */
export const TREND_MIN_POINTS = 3;
/** Trend son kaç iptal edilmemiş ölçümden hesaplanır. */
export const TREND_WINDOW = 6;

export interface TrendReading {
  measuredAt: string;
  tzOffsetMin: number;
  value: number;
  voided: boolean;
}

/** Eşik geçişinin tahmini tarihleri: yalnız gösterimdir ("Estimated (trend)"). */
export interface ConditionForecast {
  /** Uyarı bandının geçileceği gün; eğilim o yöne değilse ya da sınır yoksa `null`. */
  alertOn: LocalDate | null;
  /** Tehlike sınırının geçileceği gün. */
  dangerOn: LocalDate | null;
  /** Günlük değişim (birim/gün). */
  slopePerDay: number;
  /** Hesaba giren ölçüm sayısı. */
  basis: number;
}

const DAY_MS = 86_400_000;
const EPOCH_DAY = '1970-01-01';

/** Yerel takvimde gün sayısı (kesirli): ölçüm anı + gemi saati farkı. */
const localDays = (at: string, tzOffsetMin: number) =>
  (Date.parse(at) + tzOffsetMin * 60_000) / DAY_MS;

/**
 * Trend tahmini (`predictConditionDue`; K21): son `TREND_WINDOW` iptal edilmemiş ölçümden en
 * küçük kareler doğrusuyla uyarı ve tehlike sınırının geçileceği gün. Yalnız gösterimdir: plan
 * motorunun vadesi, gecikme ve uyum hesabı bunu kullanmaz [NR496 4.5.3a] (BV'ye sorulacak: mimari
 * belge §13.6 #2). Üçten az ölçümde, düz ya da sınırdan uzaklaşan eğilimde `undefined`; sınır
 * zaten geçilmişse `asOf` (gemi bugünü).
 */
export function predictConditionDue(
  point: MeasurementLimits,
  measurements: readonly TrendReading[],
  asOf: LocalDate,
): ConditionForecast | undefined {
  const series = measurements
    .filter((m) => !m.voided && Number.isFinite(m.value) && !Number.isNaN(Date.parse(m.measuredAt)))
    .sort((a, b) => a.measuredAt.localeCompare(b.measuredAt))
    .slice(-TREND_WINDOW);
  if (series.length < TREND_MIN_POINTS) return undefined;
  const xs = series.map((m) => localDays(m.measuredAt, m.tzOffsetMin));
  const ys = series.map((m) => m.value);
  const n = series.length;
  const meanX = xs.reduce((s, x) => s + x, 0) / n;
  const meanY = ys.reduce((s, y) => s + y, 0) / n;
  let sxx = 0;
  let sxy = 0;
  for (let i = 0; i < n; i += 1) {
    const dx = (xs[i] as number) - meanX;
    sxx += dx * dx;
    sxy += dx * ((ys[i] as number) - meanY);
  }
  if (sxx === 0) return undefined;
  const slope = sxy / sxx;
  const spread = Math.max(...ys) - Math.min(...ys);
  // Düz eğilim: değişim değerlerin ölçeğine göre ihmal edilebilir
  if (slope === 0 || Math.abs(slope) * (Math.max(...xs) - Math.min(...xs)) <= spread * 1e-9) {
    return undefined;
  }
  const intercept = meanY - slope * meanX;
  const today =
    (Date.parse(`${asOf}T00:00:00.000Z`) - Date.parse(`${EPOCH_DAY}T00:00:00.000Z`)) / DAY_MS;
  const crossing = (bound: number | null): LocalDate | null => {
    if (bound === null) return null;
    const x = (bound - intercept) / slope;
    if (x < today + 1) return asOf;
    return addDays(asOf, Math.floor(x) - today);
  };
  const [alert, danger] =
    slope > 0 ? [point.alertHigh, point.dangerHigh] : [point.alertLow, point.dangerLow];
  const alertOn = crossing(alert);
  const dangerOn = crossing(danger);
  if (alertOn === null && dangerOn === null) return undefined;
  return { alertOn, dangerOn, slopePerDay: slope, basis: n };
}
